import { type ReactElement, useState } from 'react';
import { type GetServerSideProps } from 'next';
import Link from 'next/link';
import { Locale } from '@prisma/client';
import { signIn, type SignInOptions } from 'next-auth/react';
import { Alert, Button, Checkbox, Col, Divider, Form, Input, Row } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'next-i18next';
import { type NextRouter, useRouter } from 'next/router';
import { getServerTranslations } from '~/lib/i18n/getServerTranslations';
import LanguageSwitcher from '~/components/common/LanguageSwitcher';

import type { NextPageWithLayout } from '~/pages/_app';
import { default as Layout } from '~/components/AuthLayout';
import { mapLocaleEnumToString } from '~/utils/localization';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  // Here we explicitly set the locale to the one from the cookie
  // see: https://nextjs.org/docs/advanced-features/i18n-routing#leveraging-the-next_locale-cookie
  const locale = ctx.req.cookies.NEXT_LOCALE;

  const translations = await getServerTranslations(
    locale || mapLocaleEnumToString(Locale.en_US),
    ['auth', 'common'],
  );

  return {
    props: {
      ...translations,
    },
  };
};

const getAlertTranslation = (router: NextRouter) => {
  const { error } = router.query;

  if (!error) {
    return undefined;
  }

  if (error === 'CredentialsSignin') {
    return 'auth:error.credentials';
  }

  return 'auth:error.generic';
};

const Login: NextPageWithLayout = () => {
  const { t } = useTranslation<['auth', 'common']>(['auth', 'common']);
  const router = useRouter();
  const alertTranslation = getAlertTranslation(router);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  return (
    <main className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
      <Row justify="end">
        <Col>
          <LanguageSwitcher />
        </Col>
      </Row>
      <Divider>
        <h2 className="mb-0">{t('auth:signIn')}</h2>
      </Divider>
      <Form
        name="basic"
        className="mt-5"
        initialValues={{ remember: true }}
        autoComplete="off"
        onFinish={(values: SignInOptions) => {
          setIsSubmitting(true);
          signIn('credentials', {
            ...values,
            callbackUrl: '/',
          }).catch((err) => {
            setIsSubmitting(false);
            console.log(err);
          });
        }}
      >
        <Form.Item
          name="email"
          normalize={(value: string | undefined) => value?.toLowerCase()}
          rules={[
            { required: true, message: t('auth:validation.email.required') },
            { type: 'email', message: t('auth:validation.email.invalid') },
          ]}
        >
          <Input
            prefix={<MailOutlined className="site-form-item-icon" />}
            placeholder={t('auth:form.email.label')}
            type="email"
            name="email"
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[
            { required: true, message: t('auth:validation.password.required') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder={t('auth:form.password.label')}
            name="password"
          />
        </Form.Item>
        <Form.Item>
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox>{t('auth:rememberMe')}</Checkbox>
          </Form.Item>

          <Link className="float-right" href="/forgot-password">
            {t('auth:forgotPassword')}
          </Link>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? t('auth:form.logIn.loading')
              : t('auth:form.logIn.label')}
          </Button>
        </Form.Item>
      </Form>
      {alertTranslation && (
        <Alert message={t(alertTranslation)} type="error" showIcon />
      )}
    </main>
  );
};

Login.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Login;
